import { Component, signal, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RoleFormatPipe } from '../../shared/pipes/role-format.pipe';
import { RouterLink, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { UserService } from '../../core/services/user.service';
import { User } from '../../core/services/auth.service';

@Component({
  selector: 'app-user-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    FormsModule,
    MatPaginatorModule,
    MatSortModule,
    MatIconModule,
    MatSnackBarModule,
    RoleFormatPipe
  ],
  template: `
    <div class="page-header">
      <div class="title-section">
        <h2 class="page-title">Liste des utilisateurs</h2>
        <p class="page-subtitle">Gérez les accès et les profils des collaborateurs</p>
      </div>
      <a routerLink="/users/new" class="btn btn-primary btn-with-icon">
        <mat-icon aria-hidden="true">add</mat-icon>
        Nouvel utilisateur
      </a>
    </div>

    <div class="card shadow-sm">
      <div class="table-toolbar">
        <div class="search-input-wrapper">
          <mat-icon class="search-icon" aria-hidden="true">search</mat-icon>
          <input
            type="text"
            [(ngModel)]="searchQuery"
            (ngModelChange)="onSearchChange($event)"
            placeholder="Rechercher un utilisateur..."
            class="search-input"
          >
        </div>
      </div>

      <div class="table-responsive">
        <table class="modern-table" matSort (matSortChange)="onSortChange($event)" [matSortActive]="sortField">
          <thead>
            <tr>
              <th mat-sort-header="username">Utilisateur</th>
              <th mat-sort-header="email">Contact</th>
              <th mat-sort-header="role">Rôle</th>
              <th>Statut</th>
            </tr>
          </thead>
          <tbody>
            @for (user of users(); track user.username) {
              <tr class="clickable-row" (click)="onEditUser(user)">
                <td>
                  <div class="user-cell">
                    <div class="user-avatar-small">
                      {{ user.firstName.charAt(0) }}{{ user.lastName.charAt(0) }}
                    </div>
                    <div class="user-info-text">
                      <div class="user-full-name">{{ user.firstName }} {{ user.lastName }}</div>
                      <div class="user-username">&#64;{{ user.username }}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <div class="email-cell">{{ user.email }}</div>
                </td>
                <td>
                  <span class="role-badge" [class.admin]="(user.role === 'ROLE_ADMIN' || (user.role && typeof user.role === 'object' && user.role.name === 'ROLE_ADMIN'))">
                    {{ user.role | roleFormat }}
                  </span>
                </td>
                <td>
                  <span class="status-indicator" [class.active]="user.accessEnabled">
                    <span class="dot"></span>
                    {{ user.accessEnabled ? 'Actif' : 'Inactif' }}
                  </span>
                </td>
              </tr>
            } @empty {
              <tr class="empty-row">
                <td colspan="4">
                  <div class="empty-state">
                    <mat-icon class="empty-icon" aria-hidden="true">inbox</mat-icon>
                    <p>Aucun utilisateur trouvé</p>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <mat-paginator [length]="totalElements()"
                     [pageSize]="size()"
                     [pageSizeOptions]="[5, 10, 25, 100]"
                     (page)="onPageChange($event)"
                     aria-label="Sélectionner la page">
      </mat-paginator>
    </div>
  `,
  styles: [`
    .page-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 2rem; }
    .page-title { margin: 0; font-size: 1.5rem; color: #1a237e; }
    .page-subtitle { margin: 0.25rem 0 0; color: #666; font-size: 0.9rem; }
    .btn-with-icon { display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem 1.25rem; text-decoration: none; background-color: #1a237e; color: white; border-radius: 6px; font-weight: 600; }
    .card { background: white; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 6px rgba(0,0,0,0.05); }
    .table-toolbar { padding: 1.25rem 1.5rem; border-bottom: 1px solid #eee; }
    .search-input-wrapper { position: relative; max-width: 400px; }
    .search-icon { position: absolute; left: 1rem; top: 50%; transform: translateY(-50%); color: #999; }
    .search-input { width: 100%; padding: 0.6rem 1rem 0.6rem 2.5rem; border: 1px solid #ddd; border-radius: 6px; font-size: 0.9rem; }
    .modern-table { width: 100%; border-collapse: collapse; }
    .modern-table th { text-align: left; padding: 1rem 1.5rem; background: #fcfcfd; font-size: 0.75rem; font-weight: 700; text-transform: uppercase; color: #666; border-bottom: 1px solid #eee; }
    .modern-table td { padding: 1rem 1.5rem; vertical-align: middle; border-bottom: 1px solid #eee; }
    .modern-table tr:hover { background-color: #f8f9fa; }
    .clickable-row { cursor: pointer; }
    .user-cell { display: flex; align-items: center; gap: 1rem; }
    .user-avatar-small { width: 36px; height: 36px; background-color: #e8eaf6; color: #3f51b5; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 0.8rem; }
    .user-full-name { font-weight: 600; color: #333; font-size: 0.9rem; }
    .user-username { color: #666; font-size: 0.8rem; }
    .email-cell { font-size: 0.9rem; color: #666; }
    .role-badge { display: inline-block; padding: 0.25rem 0.75rem; background-color: #e0e0e0; color: #616161; border-radius: 12px; font-size: 0.75rem; font-weight: 600; }
    .role-badge.admin { background-color: #e8eaf6; color: #3f51b5; }
    .status-indicator { display: inline-flex; align-items: center; gap: 0.5rem; font-size: 0.85rem; font-weight: 500; color: #d32f2f; }
    .status-indicator.active { color: #2e7d32; }
    .status-indicator .dot { width: 8px; height: 8px; border-radius: 50%; background-color: #d32f2f; }
    .status-indicator.active .dot { background-color: #2e7d32; }
    .empty-state { text-align: center; padding: 3rem; color: #999; }
    .empty-icon { font-size: 2rem; display: block; margin-bottom: 1rem; }
  `]
})
export class UserListComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  private userService = inject(UserService);
  private router = inject(Router);

  users = signal<User[]>([]);
  totalElements = signal(0);
  page = signal(0);
  size = signal(10);
  searchQuery = '';
  sortField = 'username';
  sortDirection = 'asc';

  private searchSubject = new Subject<string>();

  ngOnInit() {
    this.loadUsers();

    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(query => {
      this.searchQuery = query;
      this.page.set(0);
      this.loadUsers();
    });
  }

  loadUsers() {
    const sortParam = `${this.sortField},${this.sortDirection}`;
    this.userService.getUsers(this.searchQuery, this.page(), this.size(), sortParam).subscribe(response => {
      this.users.set(response.content);
      this.totalElements.set(response.totalElements);
    });
  }

  onSearchChange(value: string) {
    this.searchSubject.next(value);
  }

  onSortChange(sortState: Sort) {
    this.sortField = sortState.active;
    this.sortDirection = sortState.direction || 'asc';
    this.page.set(0);
    this.loadUsers();
  }

  onPageChange(event: PageEvent) {
    this.page.set(event.pageIndex);
    this.size.set(event.pageSize);
    this.loadUsers();
  }

  onEditUser(user: User) {
    if (user.id) {
      this.router.navigate(['/users/edit', user.id]);
    }
  }

}
